import React from 'react';
import { Tag } from 'antd';
import { Link } from 'react-router-dom';
import { Moment } from 'moment';
import { ColumnsType } from 'antd/es/table';
import { capitalize } from '@shared/utils/string';
import {
	CargoContractSides,
	DATE_AND_TIME,
	FixtureStates,
	FixtureTypeLabels,
	FixtureTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { filterUnique } from '@shared/utils/array';
import { VoyageResponse } from '@api/features/voyages/getVoyages';
import { getFixtureStateColor } from '@client/utils/colors';
import VesselDescription from '@client/components/VesselDescription';
import { formatDate } from '@client/utils/formatDate';
import { Links } from '@client/utils/links';
import styles from './VoyagesListScreen.module.css';
import { CARGO_RELET_FILTER } from './VoyagesListScreen';

export type VoyageCargoItem = {
	chartererId: number;
	chartererName: string;
	type: string;
};

export type VoyageListColumnOptions = {
	compact?: boolean;
	includeFilters?: boolean;
	filterMaps?: {
		statuses: Values<typeof FixtureStates>[];
		types: Values<typeof FixtureTypes>[];
		vessels: string[];
		identifiers: string[];
		charterers: string[];
		fixingCompanies: string[];
		fixedByFilters?: { text: string; value: number }[];
		operatedByFilters?: { text: string; value: number }[];
	};
	localFixingCompanyFilters?: string[];
	tagMaxWidth?: number;
	includeStatus?: boolean;
	linkIdentifier?: boolean;
};

export const getChartererKey = (c: VoyageCargoItem) => {
	const id = Number(c?.chartererId);

	return Number.isFinite(id) ? String(id) : null;
};

export const renderVoyageTags = (
	names: string[],
	max = 2,
	tagMaxWidth = 140,
	tagCellClassName = styles.tagCell,
) => {
	const list = (names ?? []).filter(Boolean);

	if (!list.length) {
		return '—';
	}

	const shown = list.slice(0, max);
	const rest = list.length - shown.length;
	const pillMaxWidth = shown.length === 1 ? '100%' : tagMaxWidth;

	return (
		<div className={tagCellClassName}>
			{shown.map((name, index) => (
				<Tag
					key={index}
					title={name}
					className={styles.tagPill}
					style={{ maxWidth: pillMaxWidth }}
				>
					{name}
				</Tag>
			))}
			{rest > 0 && (
				<Tag className={styles.countPill} title={list.slice(max).join(', ')}>
					+{rest}
				</Tag>
			)}
		</div>
	);
};

export const getVoyageListColumns = (
	options: VoyageListColumnOptions = {},
): ColumnsType<VoyageResponse> => {
	const {
		compact = false,
		includeFilters = false,
		filterMaps,
		localFixingCompanyFilters,
		tagMaxWidth = compact ? 90 : 140,
		includeStatus = true,
		linkIdentifier = false,
	} = options;

	const w = (full: number, small: number) => (compact ? small : full);

	const statusColumn = {
		title: 'Status',
		dataIndex: 'state',
		key: 'state',
		align: 'center' as const,
		width: w(90, 72),
		...(includeFilters && filterMaps ? {
			filterMultiple: true,
			filters: filterMaps.statuses
				.map((s) => ({ text: capitalize(s), value: s }))
				.sort((a, b) => a.text.localeCompare(b.text)),
		} : {}),
		render: (state: Values<typeof FixtureStates>) => (
			<Tag color={getFixtureStateColor(state)}>
				{capitalize(state)}
			</Tag>
		),
	};

	const getDisplayType = (row: VoyageResponse) => {
		const hasCargoRelet = row.cargos?.some(
			(cargo) => cargo.contractSide === CargoContractSides.VC_IN
		);

		if (hasCargoRelet) {
			return 'Cargo relet';
		}

		return FixtureTypeLabels[row.fixtureType];
	};

	return [
		...(includeStatus ? [statusColumn] : []),
		{
			title: 'Type',
			dataIndex: 'fixtureType',
			key: 'fixtureType',
			width: 90,
			filterMultiple: true,
			...(includeFilters && filterMaps ? {
				filterMultiple: true,
				filters: [
					...filterMaps.types
						.map((ft) => ({ text: FixtureTypeLabels[ft], value: ft }))
						.sort((a, b) => a.text.localeCompare(b.text)),
					{ text: 'Cargo relet', value: CARGO_RELET_FILTER },
				],
				onFilter: () => true,
			} : {}),
			render: (_type, row) => getDisplayType(row),
		},
		{
			title: 'Vessel name',
			dataIndex: 'vesselName',
			key: 'vesselName',
			width: w(220, 160),
			ellipsis: true,
			...(includeFilters ? { sorter: true } : {}),
			...(includeFilters && filterMaps ? {
				filters: filterMaps.vessels.map((v) => ({ text: v, value: v })),
				filterSearch: true,
				filterMultiple: true,
				onFilter: (): boolean => true,
			} : {}),
			render: (_, row) => (
				row.hasCargoRelet && row.vesselId == null ?
					'—' :
					<VesselDescription name={row.vesselName} flag={row.vesselFlag} />
			),
		},
		{
			title: 'Identifier',
			dataIndex: 'identifier',
			key: 'identifier',
			width: w(130, 100),
			ellipsis: true,
			...(includeFilters ? { sorter: true } : {}),
			...(includeFilters && filterMaps ? {
				filters: filterMaps.identifiers.map((v) => ({ text: v, value: v })),
				filterSearch: true,
				filterMultiple: true,
				onFilter: () => true,
			} : {}),
			render: (identifier: string, row) => (
				linkIdentifier ? (
					<Link
						to={Links.Voyage.get(row.id)}
						data-no-row-click="true"
						onClick={(e) => e.stopPropagation()}
					>
						{identifier}
					</Link>
				) : identifier
			),
		},
		{
			title: 'Charterer',
			key: 'charterer',
			dataIndex: 'chartererSortName',
			...(includeFilters ? { sorter: true } : {}),
			...(includeFilters && filterMaps ? {
				filters: filterMaps.charterers.map((c) => ({ text: c, value: c })),
				filterSearch: true,
				filterMultiple: true,
				onFilter: () => true,
			} : {}),
			width: w(250, 140),
			ellipsis: true,
			render: (_, row) => {
				const names =
					filterUnique(row.cargos ?? [], getChartererKey)
						.map((c) => c.chartererName?.trim())
						.filter(Boolean) as string[];

				if (names.length === 0 && row.counterpartyName) {
					names.push(row.counterpartyName);
				}

				return renderVoyageTags(names, 2, tagMaxWidth);
			},
		},
		{
			title: 'Cargos',
			key: 'cargos',
			width: w(160, 120),
			ellipsis: true,
			render: (_: unknown, row) => {
				const names: string[] = [];

				if (row.fixtureType === FixtureTypes.SPOT) {
					const cargoNames = row.hasCargoRelet ?
						[row.cargos.find((c) => c.contractSide === CargoContractSides.VC_OUT)?.type]
							.filter(Boolean) as string[] :
						filterUnique(
							row.cargos ?? [],
							(c) => c?.type?.trim()?.toLowerCase() ?? null,
						)
							.map((c) => c.type?.trim())
							.filter(Boolean) as string[];
					names.push(...cargoNames);
				}

				return renderVoyageTags(names, 2, tagMaxWidth);
			},
		},
		{
			title: <span style={{ display: 'inline-block', lineHeight: 1.1 }}>1st Load /<br />Delivery</span>,
			key: 'firstLoadOrDelivery',
			className: styles.wrapHeader,
			width: w(140, 110),
			ellipsis: true,
			render: (_, row) => {
				const names = filterUnique(
					[row.deliveryPort?.name, row.firstLoadPort].filter(Boolean) as string[],
					(x) => x.toLowerCase(),
				);

				return renderVoyageTags(names, 2, tagMaxWidth);
			},
		},
		{
			title: <span style={{ display: 'inline-block', lineHeight: 1.1 }}>Last Discharge /<br />Redelivery</span>,
			key: 'lastDisOrRedelivery',
			className: styles.wrapHeader,
			width: w(150, 110),
			ellipsis: true,
			render: (_, row) => {
				const names = filterUnique(
					[row.redeliveryPort?.name, row.lastDischargePort].filter(Boolean) as string[],
					(x) => x.toLowerCase(),
				);

				return renderVoyageTags(names, 2, tagMaxWidth);
			},
		},
		{
			title: <span style={{ display: 'inline-block', lineHeight: 1.1 }}>Commencement<br />Date</span>,
			dataIndex: 'commencementDate',
			key: 'commencementDate',
			width: w(170, 130),
			render: (value: Moment) => (
				formatDate(value, DATE_AND_TIME, true)
			),
		},
		{
			title: <span style={{ display: 'inline-block', lineHeight: 1.1 }}>Completion<br />Date</span>,
			dataIndex: 'completionDate',
			key: 'completionDate',
			width: w(170, 130),
			render: (value: Moment) => (
				formatDate(value, DATE_AND_TIME, true)
			),
		},
		{
			title: 'Fixed by',
			dataIndex: 'fixedBy',
			key: 'fixedBy',
			width: w(140, 100),
			ellipsis: true,
			...(includeFilters ? { sorter: true } : {}),
			...(includeFilters && filterMaps?.fixedByFilters ? {
				filters: filterMaps.fixedByFilters,
				filterSearch: true,
				filterMultiple: true,
				onFilter: () => true,
			} : {}),
			render: (_, row) => row.fixedByName ?? '—',
		},
		{
			title: 'Operated by',
			dataIndex: 'operatedBy',
			key: 'operatedBy',
			width: w(140, 100),
			ellipsis: true,
			...(includeFilters ? { sorter: true } : {}),
			...(includeFilters && filterMaps?.operatedByFilters ? {
				filters: filterMaps.operatedByFilters,
				filterSearch: true,
				filterMultiple: true,
				onFilter: () => true,
			} : {}),
			render: (_, row) => row.operatedByName ?? '—',
		},
		{
			title: 'Fix. Com.',
			key: 'fixingCompanyName',
			dataIndex: 'fixingCompanyName',
			width: w(140, 100),
			ellipsis: true,
			...(includeFilters ? { sorter: true } : {}),
			...(includeFilters && filterMaps ? {
				filters: filterMaps.fixingCompanies.map((c) => ({ text: c, value: c })),
				filterSearch: true,
				filterMultiple: true,
				onFilter: () => true,
			} : {}),
			...(localFixingCompanyFilters != null ? {
				sorter: (a: VoyageResponse, b: VoyageResponse) => (
					a.fixingCompanyName.localeCompare(b.fixingCompanyName)
				),
				filters: localFixingCompanyFilters.map((c) => ({ text: c, value: c })),
				filterSearch: true,
				filterMultiple: true,
				onFilter: (value, row) => row.fixingCompanyName === value,
			} : {}),
			render: (_, row) => row.fixingCompanyName || '—',
		},
	];
};
